import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { chapterApi } from '@/api/chapterApi'
import { projectApi } from '@/api/projectApi'
import { useUiStore } from '@/stores/uiStore'
import type { Asset, Chapter, Project } from '@/types/nf'

export type OnboardingStepId = 'project' | 'chapter' | 'film'
export type OnboardingStepStatus = 'done' | 'current' | 'todo'

export interface OnboardingStepProgress {
  id: OnboardingStepId
  status: OnboardingStepStatus
  route: string
}

export interface OnboardingProgress {
  projectId?: string
  chapterId?: string
  completed: boolean
  currentStepId: OnboardingStepId
  steps: OnboardingStepProgress[]
}

export interface DeriveOnboardingProgressInput {
  projects: Project[]
  chapters: Chapter[]
  assets: Asset[]
}

function readyAsset(asset: Asset, types: string[]) {
  return types.includes(String(asset.asset_type)) && ['passed', 'done'].includes(String(asset.status))
}

function hasReadyAsset(assets: Asset[], types: string[]) {
  return assets.some((asset) => readyAsset(asset, types))
}

function routeFor(step: OnboardingStepId, projectId?: string, chapterId?: string) {
  if (step === 'project' || !projectId) return '/projects'
  const encodedProject = encodeURIComponent(projectId)
  if (step === 'chapter' || !chapterId) return `/projects/${encodedProject}`
  return `/projects/${encodedProject}?tab=pipeline`
}

export function deriveOnboardingProgress({ projects, chapters, assets }: DeriveOnboardingProgressInput): OnboardingProgress {
  const project = [...projects]
    .filter((item) => !item.deleted_at)
    .sort((a, b) => (b.updated_at ?? 0) - (a.updated_at ?? 0))[0]
  const projectId = project?.id
  const doneChapter = chapters.find((chapter) => chapter.status === 'done')
  const chapterId = doneChapter?.id
  const chapterAssets = chapterId ? assets.filter((asset) => !asset.chapter_id || asset.chapter_id === chapterId) : assets
  const hasProject = Boolean(projectId)
  const hasChapterPackage = Boolean(
    doneChapter &&
      hasReadyAsset(chapterAssets, ['screenplay']) &&
      hasReadyAsset(chapterAssets, ['storyboard']),
  )
  const hasFilm = hasReadyAsset(chapterAssets, ['av_compose', 'video'])
  const completedIds = new Set<OnboardingStepId>()
  if (hasProject) completedIds.add('project')
  if (hasChapterPackage) completedIds.add('chapter')
  if (hasFilm) completedIds.add('film')

  const currentStepId: OnboardingStepId = !hasProject ? 'project' : !hasChapterPackage ? 'chapter' : !hasFilm ? 'film' : 'film'
  const completed = hasProject && hasChapterPackage && hasFilm
  const steps: OnboardingStepProgress[] = (['project', 'chapter', 'film'] as const).map((id) => ({
    id,
    status: completedIds.has(id) ? 'done' : id === currentStepId ? 'current' : 'todo',
    route: routeFor(id, projectId, chapterId),
  }))

  return { projectId, chapterId, completed, currentStepId, steps }
}

export function useOnboardingProgress(userId?: string) {
  const currentBranchId = useUiStore((s) => s.currentBranchId)
  const projectsQuery = useQuery({
    queryKey: ['projects', userId, currentBranchId],
    queryFn: () => projectApi.list(userId!),
    enabled: Boolean(userId),
  })
  const projects = projectsQuery.data ?? []
  const projectId = useMemo(
    () => [...projects].filter((project) => !project.deleted_at).sort((a, b) => (b.updated_at ?? 0) - (a.updated_at ?? 0))[0]?.id,
    [projects],
  )

  const chaptersQuery = useQuery({
    queryKey: ['chapters', userId, projectId, currentBranchId],
    queryFn: () => chapterApi.list(projectId!, currentBranchId),
    enabled: Boolean(userId && projectId),
  })
  const assetsQuery = useQuery({
    queryKey: ['project-assets', projectId, currentBranchId],
    queryFn: () => projectApi.assets(projectId!),
    enabled: Boolean(userId && projectId),
  })

  const progress = useMemo(
    () =>
      deriveOnboardingProgress({
        projects,
        chapters: chaptersQuery.data ?? [],
        assets: assetsQuery.data ?? [],
      }),
    [assetsQuery.data, chaptersQuery.data, projects],
  )
  const error = projectsQuery.error ?? chaptersQuery.error ?? assetsQuery.error
  const isLoading = projectsQuery.isLoading || chaptersQuery.isLoading || assetsQuery.isLoading

  return {
    progress,
    error,
    isLoading,
    refetch: async () => {
      const jobs: Promise<unknown>[] = [projectsQuery.refetch()]
      if (projectId) jobs.push(chaptersQuery.refetch(), assetsQuery.refetch())
      await Promise.all(jobs)
    },
  }
}
